import { useCallback } from 'react'
import { MyCourseEnum } from '@types'
import styles from './GridLessonsLayout.module.scss'
import Card from '../lesson-card/Card'
import DropBox from '../lesson-card/DropBox'
import { useDrop } from 'react-dnd'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd'
import { SllCourse, SllLesson } from '@src/api/graphql-codegen'

type Props = {
  lessons?: SllLesson[]
  source: number
  target?: string
  handleDrop?: (index: number, lesson: SllLesson) => void
  handleLeftArrowAction?: (lesson: SllLesson) => void
  handleRightArrowAction?: (lesson: SllLesson) => void
  handleOpenLesson?: (lesson: SllLesson) => void
  handleDnDOnCourseList?: (index: number, lessons: string[]) => void
  studyMode?: string
  course?: SllCourse
  handleMovableLesson?: (movable: boolean) => void
  isMovable?: boolean
  showLabel?: boolean
  handleCardHovered?: (lessonId: string, hovered: boolean) => void;
  handleSelectedCenterCard?: (centerCard: boolean) => void
  cardHovered?: boolean;
  hoveredLessonId?: string;
}

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function GridLessonsLayout(props: Props) {
  const { 
    showLabel, lessons, source, target, studyMode, course, handleDrop, handleLeftArrowAction, handleRightArrowAction, handleOpenLesson, handleDnDOnCourseList, isMovable, handleMovableLesson,
    handleCardHovered, handleSelectedCenterCard, cardHovered, hoveredLessonId
   } = props
  const onDrop = (lesson: SllLesson) => {
    handleDrop && handleDrop(source, lesson)
  }
  const handleDragEnd = useCallback((result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const updateLessons = reorder(lessons, sourceIndex, destIndex) as SllLesson[]
    const lessonIds = updateLessons.map((item: SllLesson) => {
      return item.id
    })
    if (handleDnDOnCourseList) {
      handleDnDOnCourseList(source, lessonIds)
    }
  }, [source, lessons, handleDnDOnCourseList]);

  const [{ canDrop }, drop] = useDrop({
    accept: "type",
    drop: onDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  if (source === 0) {
    return (
      <div ref={drop} className={`${studyMode === MyCourseEnum.Courses || studyMode === 'Student Introduction' ? styles.unitsDivStudyMode : styles.unitsDiv} ${canDrop ? styles.canDropbackground : ''}`}>
        {lessons?.map((lesson, index) => (
          <Card
            key={index}
            type={"type"}
            source={source}
            target={target}
            lesson={lesson}
            isDragable={studyMode === MyCourseEnum.Library}
            handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(lesson)}
            handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(lesson)}
            handleOpenLesson={handleOpenLesson}
            studyMode={studyMode}
            handleMovableLesson={handleMovableLesson}
            showLabel={showLabel}
            cardHovered={cardHovered}
            hoveredLessonId={hoveredLessonId}
            index={index+1}
            handleSelectedCenterCard={handleSelectedCenterCard}
            handleCardHovered={handleCardHovered}
          />
        ))}
      </div>
    )
  }

  return (
    <div ref={drop} className={styles.normalDiv }>
      <DropBox
        accept={"type"}
        source={source}
        onDrop={(item) => handleDrop && handleDrop(source, item)}
        course={course}
        isMovable={isMovable ?? false}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppableLesson" type="StudyPlanItems">
          {(droppableProvided: DroppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
            {lessons?.map((lesson, index) => (
              <Draggable
                key={lesson.id}
                draggableId={lesson.id}
                index={index}
              >
                {(
                  draggableProvided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => {
                return (
                  <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                    >
                    <Card 
                      draggableProvided={draggableProvided} snapshot={snapshot} key={index} type={"type"} 
                      source={source} target={target} lesson={lesson} isDragable={true}
                      handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(lesson)}
                      handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(lesson)}
                      index={index}
                    />
                  </div>
                )
              }}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default GridLessonsLayout